// B2B Customer Management
import { useState, useCallback, useMemo } from "react";
import {
  Building2,
  Plus,
  Pencil,
  AlertCircle,
  UserCheck,
  Ban,
  CreditCard,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { FilterBar } from "@/components/admin/shared/FilterBar";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useB2BCustomers,
  useB2BCustomerStats,
  useCreateB2BCustomer,
  useUpdateB2BCustomer,
  useSuspendB2BCustomer,
} from "@/hooks/admin/useB2B";
import type {
  B2BCustomer,
  B2BCustomerStatus,
  CreateB2BCustomerInput,
} from "@/lib/admin-api/b2b";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SENEGAL_REGIONS = [
  "Dakar",
  "Kaolack",
  "Saint-Louis",
  "Fatick",
  "Thiès-Mbour",
  "Diourbel-Touba",
] as const;

const STATUS_LABELS: Record<B2BCustomerStatus, string> = {
  active: "Actif",
  suspended: "Suspendu",
  pending_approval: "En attente",
};

const STATUS_COLORS: Record<B2BCustomerStatus, string> = {
  active: "bg-emerald-100 text-emerald-700",
  suspended: "bg-red-100 text-red-700",
  pending_approval: "bg-amber-100 text-amber-700",
};

const EMPTY_FORM: CreateB2BCustomerInput = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  region: "Dakar",
  credit_limit_fcfa: 0,
  payment_terms_days: 30,
  assigned_rep_id: undefined,
};

const REGION_FILTER_OPTIONS = SENEGAL_REGIONS.map((r) => ({
  value: r,
  label: r,
}));

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Actif" },
  { value: "suspended", label: "Suspendu" },
  { value: "pending_approval", label: "En attente" },
];

// ---------------------------------------------------------------------------
// Customer form modal
// ---------------------------------------------------------------------------

interface CustomerFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly customer: B2BCustomer | null;
  readonly onSave: (input: CreateB2BCustomerInput) => Promise<void>;
  readonly isSaving: boolean;
}

function CustomerFormModal({
  open,
  onClose,
  customer,
  onSave,
  isSaving,
}: CustomerFormModalProps) {
  const [form, setForm] = useState<CreateB2BCustomerInput>(
    customer
      ? {
          company_name: customer.company_name,
          contact_name: customer.contact_name,
          email: customer.email,
          phone: customer.phone,
          region: customer.region,
          credit_limit_fcfa: customer.credit_limit_fcfa,
          payment_terms_days: customer.payment_terms_days,
          assigned_rep_id: customer.assigned_rep_id ?? undefined,
        }
      : EMPTY_FORM,
  );
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setForm(EMPTY_FORM);
    setError(null);
    onClose();
  }, [onClose]);

  const update = useCallback(
    <K extends keyof CreateB2BCustomerInput>(
      key: K,
      value: CreateB2BCustomerInput[K],
    ) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!form.company_name.trim()) {
      setError("Le nom de l'entreprise est requis.");
      return;
    }
    if (!form.contact_name.trim()) {
      setError("Le nom du contact est requis.");
      return;
    }
    if (!form.phone.trim()) {
      setError("Le téléphone est requis.");
      return;
    }
    setError(null);
    try {
      await onSave(form);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [form, onSave, handleClose]);

  const inputClass =
    "h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100";
  const labelClass = "mb-1.5 block text-sm font-medium text-stone-700";

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title={customer ? "Modifier le client B2B" : "Nouveau client B2B"}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="gap-1.5">
            {isSaving && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            {customer ? "Enregistrer" : "Créer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Entreprise</label>
            <input
              type="text"
              value={form.company_name}
              onChange={(e) => update("company_name", e.target.value)}
              placeholder="Nom de l'entreprise"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Contact</label>
            <input
              type="text"
              value={form.contact_name}
              onChange={(e) => update("contact_name", e.target.value)}
              placeholder="Nom du contact"
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="contact@entreprise.com"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Téléphone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+221 7X XXX XX XX"
              className={inputClass}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Région</label>
            <select
              value={form.region}
              onChange={(e) => update("region", e.target.value)}
              className={inputClass}
            >
              {SENEGAL_REGIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Termes de paiement (jours)</label>
            <input
              type="number"
              min={0}
              value={form.payment_terms_days}
              onChange={(e) =>
                update("payment_terms_days", Number(e.target.value))
              }
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label className={labelClass}>Limite de crédit (FCFA)</label>
          <input
            type="number"
            min={0}
            value={form.credit_limit_fcfa}
            onChange={(e) => update("credit_limit_fcfa", Number(e.target.value))}
            className={inputClass}
          />
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

export default function B2BCustomers() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<
    Record<string, string | null>
  >({ region: null, status: null });
  const [editingCustomer, setEditingCustomer] = useState<B2BCustomer | null>(
    null,
  );
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const params = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      search: search || undefined,
      region: filters.region ?? undefined,
      status: (filters.status as B2BCustomerStatus) ?? undefined,
    }),
    [page, search, filters],
  );

  const { data, isLoading, isError, refetch } = useB2BCustomers(params);
  const { data: stats } = useB2BCustomerStats();
  const createCustomer = useCreateB2BCustomer();
  const updateCustomer = useUpdateB2BCustomer();
  const suspendCustomer = useSuspendB2BCustomer();

  const customers = data?.customers ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = data?.pagination.total_pages ?? 1;

  const kpiData = useMemo(
    () => ({
      total: stats?.total ?? total,
      active: stats?.active ?? 0,
      suspended: stats?.suspended ?? 0,
      creditUsed: stats?.total_credit_used_fcfa ?? 0,
    }),
    [stats, total],
  );

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value as string | null }));
    setPage(1);
  }, []);

  const handleCreateSave = useCallback(
    async (input: CreateB2BCustomerInput) => {
      await createCustomer.mutateAsync(input);
      setIsCreateOpen(false);
    },
    [createCustomer],
  );

  const handleEditSave = useCallback(
    async (input: CreateB2BCustomerInput) => {
      if (!editingCustomer) return;
      await updateCustomer.mutateAsync({ id: editingCustomer.id, input });
      setEditingCustomer(null);
    },
    [editingCustomer, updateCustomer],
  );

  const handleToggleSuspend = useCallback(
    (customer: B2BCustomer) => {
      suspendCustomer.mutate({
        id: customer.id,
        activate: customer.status === "suspended",
      });
    },
    [suspendCustomer],
  );

  const filterConfigs = useMemo(
    () => [
      {
        key: "region",
        label: "Région",
        type: "select" as const,
        options: REGION_FILTER_OPTIONS,
      },
      {
        key: "status",
        label: "Statut",
        type: "select" as const,
        options: STATUS_FILTER_OPTIONS,
      },
    ],
    [],
  );

  const columns: readonly ColumnDef<B2BCustomer>[] = useMemo(
    () => [
      {
        id: "company_name",
        header: "Entreprise",
        cell: (c) => (
          <div>
            <p className="font-medium text-stone-900">{c.company_name}</p>
            <p className="text-xs text-stone-500">{c.contact_name}</p>
          </div>
        ),
      },
      {
        id: "phone",
        header: "Téléphone",
        cell: (c) => <span className="text-sm text-stone-600">{c.phone}</span>,
      },
      {
        id: "region",
        header: "Région",
        cell: (c) => (
          <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-700">
            {c.region}
          </span>
        ),
      },
      {
        id: "credit",
        header: "Crédit utilisé / Limite",
        cell: (c) => {
          const utilization =
            c.credit_limit_fcfa > 0
              ? Math.round(((c.credit_used_fcfa ?? 0) / c.credit_limit_fcfa) * 100)
              : 0;
          const barColor =
            utilization >= 90
              ? "bg-red-500"
              : utilization >= 70
                ? "bg-amber-500"
                : "bg-emerald-500";
          return (
            <div className="min-w-[160px]">
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-medium text-stone-900">
                  {(c.credit_used_fcfa ?? 0).toLocaleString("fr-SN")}
                </span>
                <span className="text-xs text-stone-400">
                  / {(c.credit_limit_fcfa ?? 0).toLocaleString("fr-SN")} FCFA
                </span>
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <div className="h-1.5 flex-1 rounded-full bg-stone-100">
                  <div
                    className={`h-1.5 rounded-full transition-all ${barColor}`}
                    style={{ width: `${Math.min(utilization, 100)}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-stone-500">
                  {utilization}%
                </span>
              </div>
            </div>
          );
        },
      },
      {
        id: "payment_terms_days",
        header: "Termes",
        cell: (c) => (
          <span className="text-sm text-stone-600">
            {c.payment_terms_days}j
          </span>
        ),
      },
      {
        id: "total_orders",
        header: "Commandes",
        cell: (c) => (
          <span className="text-sm text-stone-600">{c.total_orders}</span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (c) => (
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[c.status]}`}
          >
            {STATUS_LABELS[c.status]}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (c) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setEditingCustomer(c)}
              className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              aria-label="Modifier"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => handleToggleSuspend(c)}
              className={`rounded p-1.5 ${
                c.status === "suspended"
                  ? "text-stone-400 hover:bg-emerald-50 hover:text-emerald-600"
                  : "text-stone-400 hover:bg-red-50 hover:text-red-600"
              }`}
              aria-label={c.status === "suspended" ? "Activer" : "Suspendre"}
            >
              {c.status === "suspended" ? (
                <UserCheck className="h-3.5 w-3.5" />
              ) : (
                <Ban className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        ),
      },
    ],
    [handleToggleSuspend],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Clients B2B
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading
                  ? "Chargement…"
                  : `${total} client(s) enregistré(s)`}
              </p>
            </div>
          </div>
          <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Nouveau client B2B
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Total clients"
            value={kpiData.total}
            format="number"
            icon={<Building2 className="h-4 w-4" />}
          />
          <KPICard
            label="Actifs"
            value={kpiData.active}
            format="number"
            icon={<UserCheck className="h-4 w-4" />}
          />
          <KPICard
            label="Suspendus"
            value={kpiData.suspended}
            format="number"
            icon={<Ban className="h-4 w-4" />}
          />
          <KPICard
            label="Crédit total utilisé"
            value={kpiData.creditUsed}
            format="currency"
            icon={<CreditCard className="h-4 w-4" />}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={search}
            onChange={(v: string) => { setSearch(v); setPage(1); }}
            placeholder="Rechercher par entreprise ou contact…"
          />
          <FilterBar
            filters={filterConfigs}
            values={filters}
            onChange={handleFilterChange}
          />
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les clients B2B.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Réessayer
            </Button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && customers.length === 0 ? (
              <EmptyState
                icon={<Building2 className="h-6 w-6" />}
                title="Aucun client B2B"
                description="Créez votre premier client B2B pour commencer."
                action={{
                  label: "Nouveau client B2B",
                  onClick: () => setIsCreateOpen(true),
                }}
              />
            ) : (
              <>
                <AdminDataTable
                  data={customers}
                  columns={columns}
                  isLoading={isLoading}
                />
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between border-t border-stone-100 pt-4">
                    <p className="text-sm text-stone-500">
                      Page {page} sur {totalPages} ({total} résultats)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        Précédent
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        Suivant
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Create modal */}
      <CustomerFormModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        customer={null}
        onSave={handleCreateSave}
        isSaving={createCustomer.isPending}
      />

      {/* Edit modal */}
      <CustomerFormModal
        open={editingCustomer !== null}
        onClose={() => setEditingCustomer(null)}
        customer={editingCustomer}
        onSave={handleEditSave}
        isSaving={updateCustomer.isPending}
      />
    </AdminLayout>
  );
}
